import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, RefreshCw, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import {
  AsyncSearchSuggestions,
  type SearchSuggestion,
} from '@/components/ui/async-search-suggestions';
import { useTableStateParams } from '@/hooks/useTableStateParams';
import { QuotationKpiRow } from '../components/QuotationKpiRow';
import { MasterDataConfigDialog } from '../components/MasterDataConfigDialog';
import { AutoDraftReviewCard } from '../components/AutoDraftReviewCard';
import { useQuotationDraftStore } from '../store/quotationDraftStore';
import {
  QuotationStatusFilterTabs,
  useQuotationFilterTab,
} from '../components/QuotationStatusFilterTabs';
import { QuotationTable } from '../components/QuotationTable';
import { quotationService } from '../services/quotationService';
import {
  QuotationStatus,
  type QuotationFilterTab,
  type QuotationListItemDto,
  type QuotationStats,
} from '../types/quotation.types';

// Two-state enum, not worth extracting from QuotationStatusBadge.
function statusLabel(s: QuotationStatus): string {
  return s === QuotationStatus.Submitted ? 'Submitted' : 'Draft';
}

interface AutoDraftListProps {
  rows: QuotationListItemDto[];
  isLoading: boolean;
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  onPageChange: (next: number) => void;
}

// Card-based view for the Auto-drafts tab — replaces the tabular layout because the
// review-card content (sender, body excerpt, attachments) doesn't fit a fixed-column row.
function AutoDraftList({
  rows,
  isLoading,
  pageIndex,
  pageSize,
  pageCount,
  onPageChange,
}: AutoDraftListProps) {
  if (isLoading && rows.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading auto-drafts…</p>;
  }
  if (!isLoading && rows.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No auto-drafts in the inbox. New RFQs ingested by the email automation pipeline will appear here.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <AutoDraftReviewCard key={row.id} quotation={row} />
      ))}
      {pageCount > 1 && (
        <div className="flex items-center justify-between border-t pt-3 text-sm text-muted-foreground">
          <span>
            Page {pageIndex + 1} of {pageCount} · {pageSize} per page
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={pageIndex === 0}
              onClick={() => onPageChange(pageIndex - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pageIndex >= pageCount - 1}
              onClick={() => onPageChange(pageIndex + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function filterTabToStatus(tab: QuotationFilterTab): QuotationStatus | undefined {
  switch (tab) {
    case 'drafts': return QuotationStatus.Draft;
    case 'submitted': return QuotationStatus.Submitted;
    case 'auto-drafts': return QuotationStatus.AutoDraft;
    case 'archived':
    case 'all':
    default: return undefined;
  }
}

export function QuotationsListPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useQuotationFilterTab();
  const { sorting, pagination, onSortingChange, onPaginationChange } = useTableStateParams({
    prefix: 'quotations',
    defaultPageSize: 10,
  });

  const [rows, setRows] = useState<QuotationListItemDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<QuotationStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const sortBy = sorting.length > 0 ? sorting[0].id : undefined;
      const sortDesc = sorting.length > 0 ? sorting[0].desc : true;
      const result = await quotationService.listPaged({
        status: filterTabToStatus(activeTab),
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy,
        sortDesc,
        includeArchived: activeTab === 'archived',
      });
      setRows(result.items);
      setTotalCount(result.totalCount);
      setTotalPages(Math.max(1, result.totalPages));
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, pagination.pageIndex, pagination.pageSize, sorting]);

  const loadStats = useCallback(async () => {
    setIsStatsLoading(true);
    try {
      setStats(await quotationService.getStats());
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleRefresh = useCallback(() => {
    void load();
    void loadStats();
  }, [load, loadStats]);

  const handleRowClick = useCallback(
    (row: QuotationListItemDto) => {
      // Open the row in the workspace — the sole edit/post surface (86d3k9589).
      // Explicit reset so a row click doesn't inherit reviewMode that the AutoDraft card's
      // Review button may have left in the store.
      useQuotationDraftStore.getState().setReviewMode(false);
      navigate(`/quotations/workspace?id=${row.id}`);
    },
    [navigate],
  );

  // Cross-status autocomplete — mirrors the picklist closed-tab pattern. Suggestions
  // are independent of the table's active filter; clicking opens the target in a new
  // tab via AsyncSearchSuggestions' built-in window.open.
  const getQuotationSuggestions = useCallback(
    async (q: string): Promise<SearchSuggestion[]> => {
      const matches = await quotationService.search(q);
      return matches.map((m) => ({
        id: String(m.id),
        title: m.quotationNumber || `Draft #${m.id}`,
        subtitle: [m.customerName, m.projectName, statusLabel(m.status)]
          .filter(Boolean)
          .join(' · '),
        href: `/quotations/workspace?id=${m.id}`,
      }));
    },
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Quotations"
        description="Draft and submitted quotations. Submit a draft to create a Cutting List in NetSuite."
        actions={
          <>
            {/* CSC-288: opt-in entry point to the master-detail workspace.
                Coexists with this list while we validate the new layout. */}
            <Button variant="outline" onClick={() => navigate('/quotations/workspace')}>
              <LayoutGrid className="h-4 w-4 mr-1.5" />
              Try the new workspace
            </Button>
            <Button variant="outline" onClick={() => setConfigOpen(true)}>
              <Settings2 className="h-4 w-4 mr-1.5" />
              Configurations
            </Button>
            {/* CSC-300: New Quotation button removed. Workspace at
                /quotations/workspace is now the canonical entry point for new
                drafts (its handleCreate + the QuotationDraftsStrip "+ New"
                affordance cover this). The "Try the new workspace" button
                above remains the explicit migration CTA. */}
          </>
        }
      />

      <QuotationKpiRow stats={stats} isLoading={isStatsLoading} />

      {/* Toolbar: search + status filter + count/refresh. Flat now that the
          page card is gone — the table below provides its own surface. */}
      <div className="flex flex-col gap-3">
        <AsyncSearchSuggestions
          getSuggestions={getQuotationSuggestions}
          placeholder="Search by number, customer, project, or cutting list..."
          inputClassName="h-9"
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <QuotationStatusFilterTabs
            value={activeTab}
            onChange={setActiveTab}
            stats={stats}
          />
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {totalCount > 0
                ? `${totalCount.toLocaleString()} quotation${totalCount !== 1 ? 's' : ''}`
                : 'No quotations'}
            </span>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {activeTab === 'auto-drafts' ? (
        <AutoDraftList
          rows={rows}
          isLoading={isLoading}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          pageCount={totalPages}
          onPageChange={(next) =>
            onPaginationChange({ pageIndex: next, pageSize: pagination.pageSize })
          }
        />
      ) : (
        <QuotationTable
          data={rows}
          isLoading={isLoading}
          sorting={sorting}
          onSortingChange={onSortingChange}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          pageCount={totalPages}
          onRowClick={handleRowClick}
        />
      )}

      <MasterDataConfigDialog open={configOpen} onOpenChange={setConfigOpen} />
    </div>
  );
}
